import { useRef, useState } from 'react'
import { AlertTriangle, Download, FileUp, Loader2 } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { Modal, errorMessage, primaryBtn, secondaryBtn } from './shared'

const TEMPLATE_CSV = [
  'code,name,domains,mailboxes',
  'NL01,Example Netherlands,example.nl;example.be,orders@example.nl',
  'GB05,Example UK,example.co.uk,',
  '',
].join('\n')

const ACTION_LABEL = { create: 'New', rename: 'Rename', unchanged: 'No change' }
const ACTION_CLS = {
  create: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  rename: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  unchanged: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
}

function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Could not read the file'))
    reader.readAsText(file)
  })
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'opcos-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

// Import the OpCo master list from a CSV: choose a file, review what it would
// do row by row, then confirm. All-or-nothing: the Import button stays off while
// any row has an error. OpCo codes are the company_ids (NL01) and are never
// changed by an import.
export default function OpCoImportModal({ onImported, onClose }) {
  const fileInput = useRef(null)
  const [fileName, setFileName] = useState('')
  const [csv, setCsv] = useState('')
  const [useRules, setUseRules] = useState(false)
  const [plan, setPlan] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const preview = async (text, rules) => {
    setBusy(true)
    setError(null)
    try {
      const res = await opcoApi.previewImport(text, { includeAutofillRules: rules })
      setPlan(res.data)
    } catch (err) {
      setPlan(null)
      setError(errorMessage(err, 'Could not read the file'))
    } finally {
      setBusy(false)
    }
  }

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    try {
      const text = await readFile(file)
      setCsv(text)
      await preview(text, useRules)
    } catch (err) {
      setPlan(null)
      setError(errorMessage(err))
    }
  }

  const toggleRules = (checked) => {
    setUseRules(checked)
    if (csv) preview(csv, checked)
  }

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await opcoApi.importCsv(csv, { includeAutofillRules: useRules })
      onImported?.(res.data)
    } catch (err) {
      // 422 carries the plan, 409 means someone changed the list meanwhile.
      if (err?.response?.data?.rows) setPlan(err.response.data)
      setError(errorMessage(err, 'Import failed'))
      setBusy(false)
    }
  }

  const s = plan?.summary
  const nothingToDo = s && s.create === 0 && s.rename === 0 && s.mappings_new === 0

  return (
    <Modal
      title="Import OpCos from CSV"
      wide
      onClose={busy ? undefined : onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy} className={secondaryBtn}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !plan?.can_apply || nothingToDo}
            className={primaryBtn}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Import
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          One row per OpCo: <span className="font-mono text-xs">code,name,domains,mailboxes</span>. The code is the
          company_id (for example NL01) and cannot be changed later. Separate several domains or mailboxes in a cell
          with <span className="font-mono text-xs">;</span> or a space.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileInput} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="opco-import-file" />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={busy} className={secondaryBtn}>
            <FileUp className="h-4 w-4" /> {fileName ? 'Choose another file' : 'Choose CSV file'}
          </button>
          {fileName && <span className="text-sm text-gray-500 dark:text-gray-400">{fileName}</span>}
          <button type="button" onClick={downloadTemplate} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-navy-700 hover:underline dark:text-navy-300">
            <Download className="h-3.5 w-3.5" /> Download template
          </button>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={useRules} onChange={(e) => toggleRules(e.target.checked)} disabled={busy} className="mt-0.5" />
          <span>Also map sender domains from existing company_id auto-fill rules (only to OpCos that exist or are in the file)</span>
        </label>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</p>
        )}

        {plan && (
          <div className="space-y-3" data-testid="opco-import-preview">
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full bg-green-100 px-2.5 py-1 text-green-700 dark:bg-green-900/30 dark:text-green-400">{s.create} new</span>
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{s.rename} renamed</span>
              <span className="rounded-full bg-navy-50 px-2.5 py-1 text-navy-700 dark:bg-navy-800/50 dark:text-navy-200">{s.mappings_new} new domains/mailboxes</span>
              {s.rows_with_errors > 0 && (
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-900/30 dark:text-red-400">{s.rows_with_errors} with errors</span>
              )}
            </div>
            {!plan.can_apply && (
              <p className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Nothing is imported until every row is valid. Fix the file and choose it again.
              </p>
            )}
            {plan.can_apply && nothingToDo && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Everything in this file already exists. Nothing to import.</p>
            )}
            {plan.warnings?.map((w) => (
              <p key={w} className="text-xs text-amber-700 dark:text-amber-400">{w}</p>
            ))}
            <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Line</th>
                    <th className="px-3 py-2">Code</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Change</th>
                    <th className="px-3 py-2">Domains / mailboxes</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.rows.map((r) => (
                    <tr key={`${r.line}-${r.code}`} className={`border-t border-gray-100 align-top dark:border-gray-800 ${r.errors.length ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                      <td className="px-3 py-2 text-gray-400">{r.line ?? 'rules'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-2">
                        {r.name}
                        {r.action === 'rename' && <div className="text-xs text-gray-400">was: {r.current_name}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ACTION_CLS[r.action]}`}>{ACTION_LABEL[r.action]}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {r.mappings.map((m) => (
                            <span
                              key={`${m.kind}-${m.value}`}
                              title={m.error || (m.source === 'autofill_rules' ? 'From an auto-fill rule' : m.status)}
                              className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                                m.status === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  : m.status === 'new' ? 'bg-navy-50 text-navy-700 dark:bg-navy-800/50 dark:text-navy-200'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                              }`}
                            >
                              {m.value}
                            </span>
                          ))}
                        </div>
                        {r.errors.map((e) => (
                          <p key={e} className="mt-1 text-xs text-red-600 dark:text-red-400">{e}</p>
                        ))}
                        {r.warnings.map((w) => (
                          <p key={w} className="mt-1 text-xs text-amber-700 dark:text-amber-400">{w}</p>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
